import { Component, inject, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { filter } from 'rxjs';

import { AnyRouteKey, hideSearchFabForRoute, Lang, matchUrlToRoute } from '../../../app.routes-config';
import { ImageFadeDirective } from '../../../directives/image-fade.directive';
import { CalendarEvent } from '../../../models/calendar-event';
import { LocalizedLinkPipe } from '../../../pipes/localized-link.pipe';
import { AnalyticsService } from '../../../services/analytics.service';
import { ApiService } from '../../../services/api.service';
import { LanguageService } from '../../../services/language.service';
import { SearchService } from '../../../services/search.service';
import { COMMUNITY_SOCIAL_URLS, CommunitySocialKey } from '../../../utils/community-social-links';
import { formatEventHour, formatEventWeekdayDate } from '../../../utils/event-date-format';
import { Dropdown } from '../dropdown/dropdown';
import { Icon } from '../icon/icon';
import { LanguageSwitcher } from '../language-switcher/language-switcher';
import { ProfileMenu } from '../profile-menu/profile-menu';
import { ThemeToggle } from '../theme-toggle/theme-toggle';

interface NavLink {
  exact?: boolean;
  labelKey: string;
  routeKey: AnyRouteKey;
}

interface MegaMenuColumn {
  headerKey: string;
  links: NavLink[];
}

interface SocialLink {
  key: CommunitySocialKey;
  labelKey: string;
  subKey: string;
  url: string;
}

@Component({
  imports: [
    RouterLink,
    RouterLinkActive,
    TranslatePipe,
    Dropdown,
    Icon,
    ImageFadeDirective,
    LanguageSwitcher,
    ProfileMenu,
    ThemeToggle,
    LocalizedLinkPipe,
  ],
  selector: 'app-site-header',
  standalone: true,
  styleUrl: './site-header.scss',
  templateUrl: './site-header.html',
})
export class SiteHeader {
  readonly searchService = inject(SearchService);

  protected readonly discordInviteUrl = COMMUNITY_SOCIAL_URLS.discord;
  protected readonly hideSearchFab = signal(false);
  protected readonly megaMenuColumns: MegaMenuColumn[] = [
    {
      headerKey: 'megaMenu.trainers',
      links: [
        { labelKey: 'nav.ranking', routeKey: 'ranking' },
        { labelKey: 'nav.trainerCodes', routeKey: 'trainerCodes' },
        { labelKey: 'nav.myProfile', routeKey: 'profile' },
      ],
    },
    {
      headerKey: 'megaMenu.pokemon',
      links: [
        { labelKey: 'nav.pokedex', routeKey: 'pokedex' },
        { labelKey: 'nav.pokedexRanking', routeKey: 'pokedexRanking' },
        { labelKey: 'nav.raidBosses', routeKey: 'raidBosses' },
        { labelKey: 'nav.weather', routeKey: 'weather' },
        { labelKey: 'nav.gymMap', routeKey: 'gymMap' },
        { labelKey: 'nav.nests', routeKey: 'nests' },
      ],
    },
    {
      headerKey: 'megaMenu.community',
      links: [
        { labelKey: 'nav.calendar', routeKey: 'calendar' },
        { labelKey: 'nav.meetups', routeKey: 'meetups' },
        { labelKey: 'megaMenu.serverList', routeKey: 'directory' },
        { labelKey: 'nav.usefulLinks', routeKey: 'usefulLinks' },
        { labelKey: 'nav.about', routeKey: 'about' },
      ],
    },
    {
      headerKey: 'megaMenu.contribute',
      links: [
        { labelKey: 'megaMenu.suggestGym', routeKey: 'suggestGym' },
        { labelKey: 'megaMenu.suggestNest', routeKey: 'suggestNest' },
        { labelKey: 'megaMenu.suggestCommunity', routeKey: 'suggestCommunity' },
      ],
    },
  ];
  protected readonly menuOpen = signal(false);
  protected readonly mobileNav: NavLink[] = [
    { exact: true, labelKey: 'nav.home', routeKey: 'home' },
    { labelKey: 'nav.raidsShort', routeKey: 'raidBosses' },
    { labelKey: 'nav.rankingShort', routeKey: 'ranking' },
    { labelKey: 'nav.pokedex', routeKey: 'pokedex' },
  ];

  protected readonly primaryNav: NavLink[] = [
    { exact: true, labelKey: 'nav.home', routeKey: 'home' },
    { labelKey: 'nav.raidsShort', routeKey: 'raidBosses' },
    { labelKey: 'nav.rankingShort', routeKey: 'ranking' },
    { labelKey: 'nav.pokedex', routeKey: 'pokedex' },
  ];

  protected readonly promoEvent = signal<CalendarEvent | null>(null);
  protected readonly promoImageFailed = signal(false);

  protected readonly socialDropdown = viewChild.required<Dropdown>('socialDropdown');

  protected readonly socialLinks: SocialLink[] = [
    {
      key: 'discord',
      labelKey: 'nav.social.discord',
      subKey: 'nav.social.discordSub',
      url: COMMUNITY_SOCIAL_URLS.discord,
    },
    {
      key: 'campfire',
      labelKey: 'nav.social.campfire',
      subKey: 'nav.social.campfireSub',
      url: COMMUNITY_SOCIAL_URLS.campfire,
    },
    {
      key: 'facebook',
      labelKey: 'nav.social.facebook',
      subKey: 'nav.social.facebookSub',
      url: COMMUNITY_SOCIAL_URLS.facebook,
    },
    {
      key: 'instagram',
      labelKey: 'nav.social.instagram',
      subKey: 'nav.social.instagramSub',
      url: COMMUNITY_SOCIAL_URLS.instagram,
    },
  ];

  private readonly analytics = inject(AnalyticsService);
  private readonly apiService = inject(ApiService);
  private readonly languageService = inject(LanguageService);
  private promoRequestedLang: Lang | null = null;

  private readonly router = inject(Router);

  constructor() {
    this.updateHideSearchFab(this.router.url);
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe(() => {
        this.closePanels();
        this.updateHideSearchFab(this.router.url);
      });
  }

  protected closeMenu(): void {
    this.menuOpen.set(false);
  }

  protected formatPromoDate(event: CalendarEvent): string {
    const start = new Date(event.start_date);
    if (Number.isNaN(start.getTime())) return '';

    const lang = this.languageService.current();

    return `${formatEventWeekdayDate(start, lang)} · ${formatEventHour(start, lang)}`;
  }

  protected onPromoImageError(): void {
    this.promoImageFailed.set(true);
  }

  protected toggleMenu(): void {
    const next = !this.menuOpen();
    this.menuOpen.set(next);
    this.socialDropdown().close();

    const lang = this.languageService.current();
    if (next && this.promoRequestedLang !== lang) {
      this.promoRequestedLang = lang;
      this.apiService
        .getCalendarEvents({ upcoming: true })
        .subscribe((events) => this.promoEvent.set(this.pickNextEvent(events)));
    }
  }

  protected toggleSocial(): void {
    this.socialDropdown().toggle();
    this.menuOpen.set(false);
  }

  protected trackPromoClick(): void {
    const event = this.promoEvent();
    if (!event) return;

    this.analytics.trackEvent('select_content', {
      content_type: 'calendar_event',
      item_id: event.id,
      location: 'header_promo',
    });
  }

  protected trackSocialCta(key: SocialLink['key']): void {
    this.analytics.trackEvent('cta_click', { cta_id: key, location: 'header_social' });
  }

  private closePanels(): void {
    this.menuOpen.set(false);
    this.socialDropdown().close();
  }

  private pickNextEvent(events: CalendarEvent[]): CalendarEvent | null {
    const now = Date.now();
    const upcoming = events
      .filter((event) => new Date(event.start_date).getTime() >= now)
      .sort((a, b) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime());

    return upcoming[0] ?? null;
  }

  private updateHideSearchFab(url: string): void {
    const match = matchUrlToRoute(url);
    this.hideSearchFab.set(match ? hideSearchFabForRoute(match.key) : false);
  }
}
